import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import { getCookieValue } from "@utils/helpers";
import axios from "axios";
import { mutate } from "swr";
import {
  sendIticketGTMTrackingEvent,
  sendGTMTrackingEvent,
} from "@utils/analyticsUtils";
import { Analytics } from "@types";
import { analytics } from "@components/analytics";
import { useTranslations } from "next-intl";

export async function postDeleteCombos(
  uniqueId: number,
  is_last_combo: boolean,
  setSuccess: (message?: string) => void,
  setFailure: (message?: string, is500?: boolean) => void,
  eventId: number,
  showingId: number,
  priceAgeId: number,
  eventName: string,
  type: string,
  price: number,
  currencyCode: string,
  analyticsConfigs?: Analytics[],
  canTrack?: boolean,
  translations?: ReturnType<
    typeof useTranslations<"Checkout.Cart.tickets.combos.delete">
  >
) {
  const basketKey = getCookieValue("ASP.NET_SessionId");
  const websiteUid = getCookieValue("itk-website-uid");

  const response = await axios.delete(
    `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/ticket/${uniqueId}`,
    {
      headers: {
        "itk-website-uid": websiteUid,
      },
    }
  );

  if (response.status >= 200 && response.status < 300) {
    setSuccess(translations?.("success") ?? "Successfully deleted combo");

    if (canTrack) {
      const trackingData = {
        event: "remove_from_cart",
        currency: currencyCode,
        value: price,
        items: [
          {
            item_id: eventId,
            sku: `${eventId}-${showingId}-${priceAgeId}`,
            item_name: eventName,
            item_variant: type,
            price: price,
            quantity: 1,
          },
        ],
      };
      sendIticketGTMTrackingEvent(trackingData);
      if (analyticsConfigs) {
        sendGTMTrackingEvent(trackingData, analyticsConfigs);
      }
    }

    if (is_last_combo) {
      await axios.delete("/api/basket");
    }
    await mutate(`basket/${basketKey}`);
    await mutate(`basket/${basketKey}/form`);
    await mutate(`basket/${basketKey}/summary`);
    await mutate(`basket/${basketKey}/checkout`);
    await mutate(
      (key: any) =>
        Array.isArray(key) && key[0] === `basket/${basketKey}/checkout`
    );
  } else if (response.status >= 400 && response.status < 500) {
    setFailure(response.data.message);
  } else {
    setFailure(translations?.("failure") ?? "Something went wrong.", true);
  }
}

async function deleteTicket(
  unique_id: number,
  websiteUid?: string,
  basketKey?: string
) {
  if (!basketKey) throw new Error("Missing basket key");
  try {
    const deletedTicket = await axios.delete(
      `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/ticket/${unique_id}`,
      {
        headers: {
          "itk-website-uid": websiteUid,
        },
      }
    );

    return deletedTicket;
  } catch (error) {
    return { error };
  }
}

export async function postDeleteTickets(
  uniqueId: number,
  is_last_ticket: boolean,
  other_tickets_to_delete: number[] | undefined,
  setSuccess: (message?: string) => void,
  setFailure: (message?: string, is500?: boolean) => void,
  eventId: number,
  showingId: number,
  eventName: string,
  priceAgeId: number,
  type: string,
  price: number,
  currencyCode: string,
  analyticsConfigs?: Analytics[],
  canTrack?: boolean,
  translations?: ReturnType<
    typeof useTranslations<"Checkout.Cart.tickets.delete">
  >
) {
  const basketKey = getCookieValue("ASP.NET_SessionId");
  const websiteUid = getCookieValue("itk-website-uid");

  let all_ok = true;
  if (other_tickets_to_delete) {
    const delete_promises = other_tickets_to_delete.map((unique_id) =>
      deleteTicket(unique_id, websiteUid, basketKey)
    );
    const deleted_tickets = await Promise.all(delete_promises);
    all_ok = deleted_tickets.every(
      (ticket) => "status" in ticket && ticket.status === 200
    );
  }
  const response = await deleteTicket(uniqueId, websiteUid, basketKey);

  if ("error" in response) {
    setFailure(translations?.("failure") ?? "Something went wrong.", true);
    return;
  }

  if (response.status >= 200 && response.status < 300 && all_ok) {
    setSuccess(translations?.("success") ?? "Successfully deleted ticket");
    if (is_last_ticket) {
      await axios.delete("/api/basket");
    }
    if (canTrack) {
      analytics.track("Product Removed", {
        product_id: uniqueId,
        sku: uniqueId,
        quantity: 1,
        currency: currencyCode,
        cart_id: basketKey,
      });

      const trackingData = {
        event: "remove_from_cart",
        currency: currencyCode,
        value:
          price *
          (other_tickets_to_delete ? other_tickets_to_delete.length + 1 : 1),
        items: [
          {
            item_id: eventId,
            sku: `${eventId}-${showingId}-${priceAgeId}`,
            item_name: eventName,
            item_variant: type,
            price: price,
            quantity: other_tickets_to_delete
              ? other_tickets_to_delete.length + 1
              : 1,
          },
        ],
      };
      sendIticketGTMTrackingEvent(trackingData);
      if (analyticsConfigs) {
        sendGTMTrackingEvent(trackingData, analyticsConfigs);
      }
    }

    await mutate(`basket/${basketKey}`);
    await mutate(`basket/${basketKey}/form`);
    await mutate(`basket/${basketKey}/summary`);
    await mutate(`basket/${basketKey}/checkout`);
    await mutate(
      (key: any) =>
        Array.isArray(key) && key[0] === `basket/${basketKey}/checkout`
    );
    await mutate(
      (key: any) => Array.isArray(key) && key[0].startsWith("events/"),
      undefined,
      {
        revalidate: true,
      }
    );
    await mutate(
      (key) => typeof key === "string" && key.startsWith("events/"),
      undefined,
      {
        revalidate: true,
      }
    );
  } else if (response.status >= 400 && response.status < 500) {
    setFailure(response.data.message);
  } else {
    setFailure(translations?.("failure") ?? "Something went wrong.", true);
  }
}
